import { Box, Typography } from '@material-ui/core';
import cn from 'classnames';
import { useFormik } from 'formik';
import * as React from 'react';
import Btn from '../../components/Btn/Btn';
import FormikFormattedTextField from '../../components/FormikInputWrappers/FormikFormattedTextField';
import IconDialog from '../../components/IconDialog/IconDialog';
import RenderText from '../../dictionaries/RenderText';
import { useBankContext, useBrandContext } from '../../dictionaries/context/BankContext';
import useDictionary from '../../dictionaries/context/useDictionary';
import { IdentityConfirmationOption, useConfirmIdentityCcNumberMutation } from '../../generated/graphql';
import { getFormikValidator } from '../../utils/getFormikValidator';
import AcceptTermsAndPolices from './AcceptTermsAndPolices';
import { IdentificationConfirmationStyles } from './IdentificationConfirmationStyles';
import ValidationAttemptsCounter from './components/ValidationAttemptsCounter';
import { requiredPoliciesAreAccepted } from './utils/requiredPoliciesAreAccepted';
import { useAttemptsExhaustedRedirect } from './utils/useAttemptsExhaustedRedirect';
import { useIdentityConfirmation } from './utils/useIdentityConfirmation';
import { useLegalPolicyAccepted } from './utils/useLegalAcceptance';

interface IdentificationConfirmationCardProps {
  baseClasses: IdentificationConfirmationStyles;
  parentUrl: string;
  option: IdentityConfirmationOption;
  refetchConfirmationOptions?: () => Promise<any>;
  setShowSnackbarCardError?: (show: boolean) => void;
}

const initialValues = {
  digits: '',
  policiesAndTermsA: false,
  policiesAndTermsB: false,
};

const IdentificationConfirmationCard: React.FC<IdentificationConfirmationCardProps> = (props) => {
  const {
    baseClasses,
    parentUrl,
    option,
    refetchConfirmationOptions,
    setShowSnackbarCardError } = props;
  const { gT } = useDictionary();

  const [ brand ] = useBrandContext();
  const [ bank ] = useBankContext();
  const policyUpToDate = useLegalPolicyAccepted();

  // Redirigir si los intentos se agotaron
  useAttemptsExhaustedRedirect(
    option,
    parentUrl,
    setShowSnackbarCardError,
  );

  const [ confirmIdentityCCNumber, { loading } ] = useIdentityConfirmation(
    useConfirmIdentityCcNumberMutation,
    'confirmIdentityCCNumber',
    `${parentUrl}/verificar-celular`,
    {
      onError: async () => {
        if (refetchConfirmationOptions) await refetchConfirmationOptions();
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        formik.setErrors({ digits: 'Número incorrecto' });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    validate: getFormikValidator({
      digits: 'required|digits:4',
    }, undefined, undefined, {
      required: 'Debes ingresar 4 números',
      digits: 'Debes ingresar 4 números',
    }),
    onSubmit: ({
      digits,
      policiesAndTermsA: termsAndConditions,
      policiesAndTermsB: dataProcessing,
    }) => {
      return confirmIdentityCCNumber({
        variables: {
          digits,
          brand: brand!,
          bank: bank!,
          termsAndConditions,
          dataProcessing,
        },
      });
    },
  });

  const disableSubmitButton = !formik.isValid
    || !requiredPoliciesAreAccepted(formik.values, policyUpToDate);

  return (
    <>
      <Typography className={cn(baseClasses.title, 'mb-2')}>
        <RenderText textKey="validation_tc_title" />
      </Typography>

      <Typography className={cn(baseClasses.text, 'mt-2 mb-2')}>
        <RenderText textKey="validation_tc_description" />
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <FormikFormattedTextField
          valueId="digits"
          formik={formik}
          fullWidth
          placeholder={gT('validation_tc_digits_placeholder') as string}
          label={gT('validation_tc_digits_label')}
          disableAutoComplete
          format={{
            format: '####',
          }}
        />

        <ValidationAttemptsCounter
          remainingAttempts={option?.remainingAttempts}
        />

        {!policyUpToDate ? <AcceptTermsAndPolices formik={formik} /> : null}

        <Box display="flex" justifyContent="space-between" my={3}>
          <Btn
            color="primary"
            disabled={disableSubmitButton}
            type="submit"
            className="ml-auto"
            data-testid="identification-confirmation-card__continue-button"
          >
            <RenderText textKey="validation_tc_next_button" />
          </Btn>
        </Box>
      </form>

      <IconDialog
        icon="loading"
        rotating
        open={formik.isSubmitting || loading}
      >
        <Typography variant="h3"><RenderText textKey="validation_tc_loading" /></Typography>
      </IconDialog>
    </>
  );
};

export default IdentificationConfirmationCard;
